import { useEffect, useState } from "react";

export type StepDirection = "forward" | "back";

/** Premium easing curves for natural, high-end motion */
export const EASE_OUT_EXPO = [0.16, 1, 0.3, 1] as const;
export const EASE_IN_OUT = [0.45, 0, 0.15, 1] as const;

export const DURATION = {
  fast: 0.28,
  normal: 0.5,
  slow: 0.65,
  step: 0.55,
} as const;

/** Gentle spring for the desktop calendar sliding between arrival and departure */
export const CALENDAR_POPOVER_SPRING = {
  type: "spring" as const,
  stiffness: 210,
  damping: 24,
  mass: 0.95,
};

export const CALENDAR_POPOVER_ENTER = {
  type: "spring" as const,
  stiffness: 280,
  damping: 26,
  mass: 0.9,
};

export function usePrefersReducedMotion(): boolean {
  const [reduced, setReduced] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  });

  useEffect(() => {
    const media = window.matchMedia("(prefers-reduced-motion: reduce)");
    const onChange = () => setReduced(media.matches);
    media.addEventListener("change", onChange);
    return () => media.removeEventListener("change", onChange);
  }, []);

  return reduced;
}

export const stepVariants = {
  enter: (direction: StepDirection) => ({
    opacity: 0,
    x: direction === "forward" ? 36 : -36,
    y: 12,
    scale: 0.975,
  }),
  center: {
    opacity: 1,
    x: 0,
    y: 0,
    scale: 1,
  },
  exit: (direction: StepDirection) => ({
    opacity: 0,
    x: direction === "forward" ? -28 : 28,
    y: -6,
    scale: 0.985,
  }),
};

export const fadeUpVariants = {
  hidden: { opacity: 0, y: 16 },
  visible: { opacity: 1, y: 0 },
};

export const fadeInVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1 },
};

export const scaleInVariants = {
  hidden: { opacity: 0, scale: 0.92 },
  visible: { opacity: 1, scale: 1 },
};

export const staggerContainer = {
  hidden: {},
  visible: {
    transition: { staggerChildren: 0.08, delayChildren: 0.06 },
  },
};

export const staggerItem = {
  hidden: { opacity: 0, y: 18 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: DURATION.normal, ease: EASE_OUT_EXPO },
  },
};
